/**
 * The apron view: the airfield as it is drawn, with every stand where it is.
 *
 * The runways come from the survey, the taxiways and their centrelines from
 * the derived pavement every map on the platform uses, the stands from the
 * aerodrome plan (`apronLayout.ts`), and the scanned sheet itself can be laid
 * underneath. On it, each stand shows what is on it at the moment the time
 * bar is set to — the aircraft, at its size, nose to the pier — and what is
 * wrong with it: a clash, a closure from a work order, work nearby.
 *
 * ── assigning ────────────────────────────────────────────────────────────
 *
 * Drag a flight from the list onto a stand, on the map or on the timeline, or
 * pick a flight and then a stand. While a flight is in hand every stand says
 * at once whether it would take it: green and ranked where it is clear, red
 * with the reason where it is not. A clear drop is made and can be undone; a
 * drop onto a problem asks first and names the problem. An aircraft already
 * on a stand is moved the same way. "Place them all" proposes a stand for
 * every visit still waiting, each placed before the next is considered, and
 * nothing is changed until the proposal is accepted.
 *
 * The rules are the register's own four. Nothing here decides differently
 * from the board beneath it; it only lets a person see the plan on the ground
 * and change it with their hands.
 */
import { useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import { Link } from "react-router-dom";
import { bestKnown, flightName, SIZE_ORDER, type Flight, type SizeCode } from "../../domain/flights";
import {
  candidatesFor,
  STAND_KIND_LABEL,
  type Stand,
  type StandAssignment,
  type StandBlock,
  type StandKind,
  type StandRegister,
  type StandRow,
} from "../../domain/stands";
import {
  AREA_LABEL,
  bridgeFoot,
  gatePoint,
  noseAngle,
  pierOutline,
  PIVOT,
  ROT,
  turn,
  runways,
  SHEET,
  SPAN,
  STAND_SPOTS,
  terminalOutline,
  toX,
  toY,
  VIEWS,
  keepOnStands,
  nearestStand,
  standsInView,
  type StandSpot,
  type ViewBox,
} from "../../domain/apronLayout";
import {
  assign,
  assignmentOf,
  checkPlacement,
  occupancyFor,
  pairOf,
  proposeAll,
  release,
  standAt,
  type Placement,
  type Proposal,
} from "../../domain/standPlanning";
import { opsState } from "../../domain/flightDelays";
import { carrierHue } from "../../domain/fidsPublic";
import { Button, Card, EmptyState, Field, Input, Select } from "../../design/components";
import { t, tn } from "../../i18n";
import { hhmm } from "../../lib/format";
import a from "./ApronView.module.css";
import centrelines from "../../data/twy-centrelines.json";

const MIN = 60_000;
/* one literal, so the single-file build inlines the raster once */
const PAVEMENT = "/assets/pavement.png";

/*
 * The rasters are coloured ONCE, here, and handed to the map as plain
 * pictures. Colouring them with SVG or CSS filters looked the same and re-ran
 * over millions of pixels on every step of a pan or a zoom — which was the lag.
 */
const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });

async function toUrl(c: HTMLCanvasElement): Promise<string> {
  const blob = await new Promise<Blob | null>((res) => c.toBlob(res));
  return blob ? URL.createObjectURL(blob) : c.toDataURL();
}

let pavementLayers: Promise<{ asphalt: string } | null> | null = null;
/** The taxiways in asphalt, from the derived pavement's taxiway channel. The centrelines are drawn as lines. */
function paintedPavement() {
  pavementLayers ??= (async () => {
    try {
      const img = await loadImage(PAVEMENT);
      const w = img.width;
      const h = img.height;
      const src = document.createElement("canvas");
      src.width = w;
      src.height = h;
      const g = src.getContext("2d", { willReadFrequently: true });
      if (!g) return null;
      g.drawImage(img, 0, 0);
      const d = g.getImageData(0, 0, w, h).data;
      const layer = async (channel: number, rgb: [number, number, number]) => {
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        const cg = c.getContext("2d")!;
        const out = cg.createImageData(w, h);
        for (let j = 0; j < w * h; j++) {
          const alpha = d[j * 4 + channel];
          if (!alpha) continue;
          out.data[j * 4] = rgb[0];
          out.data[j * 4 + 1] = rgb[1];
          out.data[j * 4 + 2] = rgb[2];
          out.data[j * 4 + 3] = alpha;
        }
        cg.putImageData(out, 0, 0);
        return toUrl(c);
      };
      return { asphalt: await layer(0, [43, 54, 66]) };
    } catch {
      return null;
    }
  })();
  return pavementLayers;
}

let darkSheet: Promise<string | null> | null = null;
/**
 * The scanned sheet, dark: lightness turned over and the colours kept, at a
 * size a screen can use. Borrowed from the Interactive Map's copy, not loaded twice.
 */
function darkDrawing() {
  darkSheet ??= (async () => {
    try {
      const m = await import("../../demo/domain/mapHost");
      await m.loadRasters();
      if (!m.base) return null;
      const scale = Math.min(1, 1800 / m.base.width);
      const w = Math.round(m.base.width * scale);
      const h = Math.round(m.base.height * scale);
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const g = c.getContext("2d", { willReadFrequently: true });
      if (!g) return null;
      g.drawImage(m.base, 0, 0, w, h);
      const im = g.getImageData(0, 0, w, h);
      const px = im.data;
      for (let j = 0; j < px.length; j += 4) {
        const L = 0.299 * px[j] + 0.587 * px[j + 1] + 0.114 * px[j + 2];
        const shift = (255 - L - L) * 0.9;
        px[j] = Math.max(0, Math.min(255, px[j] * 0.9 + shift));
        px[j + 1] = Math.max(0, Math.min(255, px[j + 1] * 0.9 + shift));
        px[j + 2] = Math.max(0, Math.min(255, px[j + 2] * 0.9 + shift));
      }
      g.putImageData(im, 0, 0);
      return toUrl(c);
    } catch {
      return null;
    }
  })();
  return darkSheet;
}

/*
 * The taxiway centrelines as lines, not as a picture: joined, straightened and
 * rounded once by scripts/build-centrelines.mjs, so they read as cleanly as
 * the stand lead-in lines at any zoom.
 */
const CENTRELINES: string[] = (centrelines as unknown as { lines: [number, number][][] }).lines.map(
  (l) => "M" + l.map(([E, N]) => `${toX(E).toFixed(1)},${toY(N).toFixed(1)}`).join("L"),
);

/** Text grows a little as the map zooms out, so a number stays readable at every distance. */
const textScale = (b: ViewBox) => String(Math.min(3.2, Math.max(0.5, b.w / 1500)));
type ViewKey = keyof typeof VIEWS;
type Selected = { kind: "stand"; code: string } | { kind: "flight"; id: string } | null;

interface Drag {
  flightId: string;
  x: number;
  y: number;
  over: string | null;
  moved: boolean;
  /** where the press began, so a click is not taken for a drag */
  sx: number;
  sy: number;
}

interface Verdict {
  clear: boolean;
  rank?: number;
  why: string;
}

export interface ApronViewProps {
  register: StandRegister;
  flights: readonly Flight[];
  blocks: readonly StandBlock[];
  rows: readonly StandRow[];
  /** every flight still waiting for a stand */
  waiting: readonly Flight[];
  who: string;
  /**
   * Save a changed register. `base` is the register the change was made
   * from (an Undo passes what was saved, so only that change is reversed);
   * `force: false` refuses a placement the plan would refuse.
   */
  onSave: (next: StandRegister, opts?: { base?: StandRegister; force?: boolean }) => void | Promise<void>;
}

/* a parked airliner, nose at the origin pointing +x, one unit of span */
const AIRCRAFT =
  "M0,0 C-0.02,-0.035 -0.05,-0.05 -0.09,-0.05 L-0.36,-0.05 L-0.62,-0.5 L-0.7,-0.5 L-0.56,-0.05 L-0.86,-0.05 L-0.95,-0.18 L-1,-0.18 L-0.96,-0.04 L-0.98,0 L-0.96,0.04 L-1,0.18 L-0.95,0.18 L-0.86,0.05 L-0.56,0.05 L-0.7,0.5 L-0.62,0.5 L-0.36,0.05 L-0.09,0.05 C-0.05,0.05 -0.02,0.035 0,0 Z";

/* the closest and the farthest the map goes: a few stands, and the whole airfield */
const MIN_W = 260;
const maxW = () => VIEWS.all.box().w * 1.15;
/** Wheel and pinch speed: how much one unit of wheel travel zooms. Low, so a trackpad does not race. */
const WHEEL_RATE = 0.0011;

/**
 * Scale a view about a point, keeping its shape. Width and height scale by the
 * same factor, so the view never stretches and the point under the pointer
 * stays under it.
 */
function scaleAbout(b: ViewBox, f: number, cx: number, cy: number): ViewBox {
  const w = Math.min(Math.max(b.w * f, MIN_W), maxW());
  const k = w / b.w;
  return { x: cx - (cx - b.x) * k, y: cy - (cy - b.y) * k, w, h: b.h * k };
}

export function ApronView({ register, flights, blocks, rows, waiting, who, onSave }: ApronViewProps) {
  const [viewKey, setViewKey] = useState<ViewKey>("pier");
  const [box, setBox] = useState<ViewBox>(() => VIEWS.pier.box());
  const [live, setLive] = useState(true);
  const [atMs, setAtMs] = useState(() => Date.now());
  const [playing, setPlaying] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [labels, setLabels] = useState(true);
  const [selected, setSelected] = useState<Selected>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [confirm, setConfirm] = useState<Placement | null>(null);
  const [proposal, setProposal] = useState<{ proposals: Proposal[]; nowhere: Flight[] } | null>(null);
  /* `undo` is the register before the change, `done` the one saved; an Undo reverses one into the other */
  const [toast, setToast] = useState<{
    text: string;
    undo?: StandRegister;
    done?: StandRegister;
    pending?: boolean;
    failed?: boolean;
  } | null>(null);
  const [drawingSrc, setDrawingSrc] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [full, setFull] = useState(false);
  const fullRef = useRef<HTMLDivElement>(null);
  /*
   * The view as it is now. Pan, zoom and pinch move the map by setting its
   * viewBox directly, which the browser does in a frame; the rest of the view
   * (which stands are drawn, the timeline) catches up once the hand stops.
   */
  const boxRef = useRef(box);
  const settle = useRef<number | null>(null);
  function applyBox(next: ViewBox, now = false) {
    /* never past the stands, never a view with no usable numbers */
    const b = keepOnStands(next, boxRef.current);
    boxRef.current = b;
    const el = svgRef.current;
    if (el) {
      el.setAttribute("viewBox", `${b.x} ${b.y} ${b.w} ${b.h}`);
      el.style.setProperty("--k", textScale(b));
    }
    if (settle.current) clearTimeout(settle.current);
    if (now) setBox(b);
    else settle.current = window.setTimeout(() => setBox(boxRef.current), 160);
  }
  const dragRef = useRef<Drag | null>(null);
  const ghostRef = useRef<HTMLDivElement>(null);

  /* the clock: live follows now; play runs the day forward five minutes a beat */
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => setAtMs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [live]);
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setAtMs((m) => m + 5 * MIN), 350);
    return () => clearInterval(id);
  }, [playing]);
  const at = useMemo(() => new Date(atMs), [atMs]);

  /* the scanned sheet, darkened once */
  useEffect(() => {
    if (!drawing || drawingSrc) return;
    let gone = false;
    void darkDrawing().then((u) => !gone && u && setDrawingSrc(u));
    return () => {
      gone = true;
    };
  }, [drawing, drawingSrc]);
  const [pavement, setPavement] = useState<{ asphalt: string } | null>(null);
  useEffect(() => {
    let gone = false;
    void paintedPavement().then((p) => !gone && p && setPavement(p));
    return () => {
      gone = true;
    };
  }, []);

  useEffect(() => {
    /* a save still on its way stays up until it lands */
    if (!toast || toast.pending) return;
    const id = setTimeout(() => setToast(null), toast.failed ? 15000 : 9000);
    return () => clearTimeout(id);
  }, [toast]);

  const byCode = useMemo(() => new Map(register.stands.map((s) => [s.code, s])), [register.stands]);
  const rowByCode = useMemo(() => new Map(rows.map((r) => [r.stand.code, r])), [rows]);
  const flightById = useMemo(() => new Map(flights.map((f) => [f.id, f])), [flights]);
  const offDrawing = useMemo(
    () => register.stands.filter((s) => !STAND_SPOTS.some((p) => p.code === s.code)),
    [register.stands],
  );

  /* one card per visit still waiting */
  const queue = useMemo(() => {
    const seen = new Set<string>();
    const out: { flight: Flight; label: string; from: string; to: string }[] = [];
    for (const f of waiting) {
      if (assignmentOf(register, f, flights)) continue;
      const occ = occupancyFor(f, flights);
      if (!occ || seen.has(occ.flightId)) continue;
      seen.add(occ.flightId);
      out.push({ flight: flightById.get(occ.flightId) ?? f, label: occ.label, from: occ.from, to: occ.to });
    }
    return out;
  }, [waiting, register, flights, flightById]);

  /* the flight in hand: dragged, or picked */
  const inHand = drag ? flightById.get(drag.flightId) : selected?.kind === "flight" ? flightById.get(selected.id) : undefined;
  const verdicts = useMemo(() => {
    const out = new Map<string, Verdict>();
    if (!inHand) return out;
    const occ = occupancyFor(inHand, flights);
    if (!occ) return out;
    const visit = flightById.get(occ.flightId) ?? inHand;
    const own = assignmentOf(register, inHand, flights);
    const reg = own ? release(register, own.id) : register;
    const ranked = candidatesFor({ register: reg, flights, blocks, flight: visit, window: { from: occ.from, to: occ.to } });
    let rank = 0;
    for (const c of ranked) {
      if (c.clear) rank += 1;
      out.set(c.stand.code, {
        clear: c.clear,
        rank: c.clear && rank <= 3 ? rank : undefined,
        why: c.clear ? c.because : c.conflicts.map((x) => x.why).join(" "),
      });
    }
    return out;
  }, [inHand, flights, flightById, register, blocks]);

  /* ── changing the plan ──────────────────────────────────────────────── */

  /*
   * A change is shown as saving until the platform has it; only then does it
   * say what was done and offer Undo. A refused or failed save says why, here
   * where the person acted — full-screen included.
   */
  function send(next: StandRegister, opts: { base?: StandRegister; force?: boolean } | undefined, after: (ok: boolean, why?: string) => void) {
    let out: void | Promise<void>;
    try {
      out = onSave(next, opts);
    } catch (e) {
      after(false, e instanceof Error ? e.message : String(e));
      return;
    }
    Promise.resolve(out).then(
      () => after(true),
      (e: unknown) => after(false, e instanceof Error ? e.message : String(e)),
    );
  }

  /* an added stand has no Undo: the platform keeps a register entry once made */
  function commit(next: StandRegister, text: string, undoable = true) {
    const before = register;
    setToast({ text: t("Saving…"), pending: true });
    send(next, undefined, (ok, why) =>
      setToast(
        ok
          ? undoable
            ? { text, undo: before, done: next }
            : { text }
          : { text: t("Not saved: {why}", { why: why ?? "" }), failed: true },
      ),
    );
  }

  function undoLast(undo: StandRegister, done: StandRegister) {
    setToast({ text: t("Undoing…"), pending: true });
    /* only what this change did is reversed, and not forced over anyone placed since */
    send(undo, { base: done, force: false }, (ok, why) =>
      setToast(ok ? { text: t("Undone.") } : { text: t("Could not undo: {why}", { why: why ?? "" }), failed: true }),
    );
  }

  function dropOn(flight: Flight, code: string) {
    const stand = byCode.get(code);
    if (!stand) {
      setSelected({ kind: "stand", code });
      setToast({ text: t("Stand {code} is on the drawing but not in the register. Add it first.", { code }) });
      return;
    }
    const p = checkPlacement(register, flights, blocks, flight, code);
    if (!p) {
      setToast({ text: t("{flight} has no time on the record, so there is nothing to hold a stand for.", { flight: flightName(flight) }) });
      return;
    }
    const own = assignmentOf(register, flight, flights);
    if (own?.standCode === code) return;
    if (p.conflicts.length) {
      setConfirm(p);
      return;
    }
    commit(
      assign(register, flights, flight, code, who),
      own
        ? t("{flight} moved from {from} to {to}.", { flight: occupancyFor(flight, flights)?.label ?? flightName(flight), from: own.standCode, to: code })
        : t("{flight} is on stand {to}.", { flight: occupancyFor(flight, flights)?.label ?? flightName(flight), to: code }),
    );
    setSelected({ kind: "stand", code });
  }

  function pickStand(code: string) {
    if (selected?.kind === "flight") {
      const f = flightById.get(selected.id);
      if (f) {
        dropOn(f, code);
        return;
      }
    }
    setSelected({ kind: "stand", code });
  }

  /* ── dragging a flight, from the list, the timeline or the map ──────── */

  function startDrag(e: RPointerEvent, flightId: string) {
    if (e.button !== 0) return;
    e.stopPropagation();
    const d = { flightId, x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, over: null, moved: false };
    dragRef.current = d;
    setDrag(d);
  }
  useEffect(() => {
    if (!drag) return;
    const standUnder = (x: number, y: number) =>
      (document.elementFromPoint(x, y)?.closest("[data-stand]") as HTMLElement | SVGElement | null)?.dataset.stand ?? null;
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const next = {
        ...d,
        x: e.clientX,
        y: e.clientY,
        moved: d.moved || Math.hypot(e.clientX - d.sx, e.clientY - d.sy) > 6,
        over: standUnder(e.clientX, e.clientY),
      };
      dragRef.current = next;
      if (ghostRef.current) ghostRef.current.style.transform = `translate(${e.clientX + 12}px, ${e.clientY + 12}px)`;
      /* the screen changes only when the stand under the pointer does */
      if (next.over !== d.over || next.moved !== d.moved) setDrag(next);
    };
    const up = (e: PointerEvent) => {
      const d = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!d) return;
      const f = flightById.get(d.flightId);
      if (!f) return;
      if (!d.moved) {
        setSelected((s) => (s?.kind === "flight" && s.id === f.id ? null : { kind: "flight", id: f.id }));
        return;
      }
      const code = standUnder(e.clientX, e.clientY);
      if (code) dropOn(f, code);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      dragRef.current = null;
      setDrag(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up, { once: true });
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("keydown", key);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the drag's own start is the trigger
  }, [drag?.flightId]);

  /* ── panning and zooming the map ────────────────────────────────────── */

  const pan = useRef<{ x: number; y: number; box: ViewBox; per: number } | null>(null);
  function unitsPerPixel(): number {
    const el = svgRef.current;
    if (!el) return 1;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return 1;
    /* the view as it is now, not as it was at the last render: right after a zoom they differ */
    const b = boxRef.current;
    return Math.max(b.w / r.width, b.h / r.height);
  }
  /* two fingers pinch; one finger or the mouse pans */
  const touches = useRef(new Map<number, [number, number]>());
  const pinch = useRef<{ d: number } | null>(null);
  function onMapDown(e: RPointerEvent<SVGSVGElement>) {
    if (e.button !== 0 || drag) return;
    touches.current.set(e.pointerId, [e.clientX, e.clientY]);
    (e.target as Element).setPointerCapture?.(e.pointerId);
    if (touches.current.size === 2) {
      const [p, q] = [...touches.current.values()];
      pinch.current = { d: Math.hypot(p[0] - q[0], p[1] - q[1]) };
      pan.current = null;
      return;
    }
    pan.current = { x: e.clientX, y: e.clientY, box: boxRef.current, per: unitsPerPixel() };
  }
  function onMapMove(e: RPointerEvent<SVGSVGElement>) {
    if (touches.current.has(e.pointerId)) touches.current.set(e.pointerId, [e.clientX, e.clientY]);
    if (pinch.current && touches.current.size === 2) {
      const [p, q] = [...touches.current.values()];
      const d = Math.hypot(p[0] - q[0], p[1] - q[1]);
      if (d > 0 && pinch.current.d > 0) {
        const [cx, cy] = toMap((p[0] + q[0]) / 2, (p[1] + q[1]) / 2);
        const f = pinch.current.d / d;
        pinch.current = { d };
        applyBox(scaleAbout(boxRef.current, f, cx, cy));
      }
      return;
    }
    const pp = pan.current;
    if (!pp) return;
    applyBox({ ...pp.box, x: pp.box.x - (e.clientX - pp.x) * pp.per, y: pp.box.y - (e.clientY - pp.y) * pp.per });
  }
  function onMapUp(e?: RPointerEvent<SVGSVGElement>) {
    if (e) touches.current.delete(e.pointerId);
    if (touches.current.size < 2) pinch.current = null;
    pan.current = null;
  }
  /* a glide from one view to another, so the eye can follow where it went */
  const glide = useRef<number | null>(null);
  function animateTo(target: ViewBox, ms = 320) {
    if (glide.current) cancelAnimationFrame(glide.current);
    const from = boxRef.current;
    const start = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / ms);
      const e = 1 - Math.pow(1 - p, 3);
      applyBox(
        {
          x: from.x + (target.x - from.x) * e,
          y: from.y + (target.y - from.y) * e,
          w: from.w + (target.w - from.w) * e,
          h: from.h + (target.h - from.h) * e,
        },
        p >= 1,
      );
      glide.current = p < 1 ? requestAnimationFrame(step) : null;
    };
    glide.current = requestAnimationFrame(step);
  }
  function zoom(f: number) {
    const b = boxRef.current;
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    let next = scaleAbout(b, f, cx, cy);
    /* zooming in on empty ground (the middle of "All stands") goes towards the nearest stand instead */
    if (f < 1 && standsInView(next) === 0) {
      const s = nearestStand(cx, cy);
      next = scaleAbout(b, f, s.x, s.y);
      if (standsInView(next) === 0) next = { ...next, x: s.x - next.w / 2, y: s.y - next.h / 2 };
    }
    animateTo(next, 220);
  }
  /** Where a screen point falls in the map's own units. */
  function toMap(clientX: number, clientY: number): [number, number] {
    const el = svgRef.current;
    const b = boxRef.current;
    if (!el) return [b.x + b.w / 2, b.y + b.h / 2];
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return [b.x + b.w / 2, b.y + b.h / 2];
    const per = Math.max(b.w / r.width, b.h / r.height);
    const ox = (r.width - b.w / per) / 2;
    const oy = (r.height - b.h / per) / 2;
    return [b.x + (clientX - r.left - ox) * per, b.y + (clientY - r.top - oy) * per];
  }
  /*
   * The wheel. A trackpad sends dozens of small events per gesture, a mouse a
   * few large ones; both are summed and applied once a frame, at a gentle
   * rate, about the point under the pointer.
   */
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    let pending = 0;
    let at: [number, number] = [0, 0];
    let frame: number | null = null;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      if (glide.current) {
        cancelAnimationFrame(glide.current);
        glide.current = null;
      }
      /* lines and pages into pixels; a pinch on a trackpad arrives as ctrl+wheel with small deltas */
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
      pending += Math.max(-120, Math.min(120, e.deltaY * unit * (e.ctrlKey ? 4 : 1)));
      at = [e.clientX, e.clientY];
      if (frame === null)
        frame = requestAnimationFrame(() => {
          frame = null;
          const [cx, cy] = toMap(at[0], at[1]);
          const f = Math.exp(pending * WHEEL_RATE);
          pending = 0;
          applyBox(scaleAbout(boxRef.current, f, cx, cy));
        });
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", wheel);
      if (frame !== null) cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads the view through boxRef
  }, [full]);

  function goTo(k: ViewKey) {
    setViewKey(k);
    animateTo(VIEWS[k].box());
  }
  function goToStand(code: string) {
    const s = STAND_SPOTS.find((p) => p.code === code);
    if (!s) return;
    const [x, y] = turn(toX(s.E), toY(s.N));
    animateTo({ x: x - 300, y: y - 170, w: 600, h: 340 });
  }

  /* Esc puts down what is picked, before it leaves full screen */
  useEffect(() => {
    if (!selected) return;
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || confirm || proposal || dragRef.current) return;
      e.stopImmediatePropagation();
      setSelected(null);
    };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  }, [selected, confirm, proposal]);

  /* full screen: the browser's own where the page may have it, the whole window where not */
  useEffect(() => {
    if (!full) return;
    fullRef.current?.requestFullscreen?.().catch(() => undefined);
    const key = (e: KeyboardEvent) => e.key === "Escape" && !confirm && !proposal && setFull(false);
    const left = () => !document.fullscreenElement && setFull(false);
    window.addEventListener("keydown", key);
    document.addEventListener("fullscreenchange", left);
    /* the bigger map shows more of the same view */
    return () => {
      window.removeEventListener("keydown", key);
      document.removeEventListener("fullscreenchange", left);
      if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => undefined);
    };
  }, [full, confirm, proposal]);

  /* ── the parts ──────────────────────────────────────────────────────── */

  const shown = useMemo(() => {
    const m = Math.max(200, box.w * 0.6);
    return STAND_SPOTS.filter((p) => {
      const [x, y] = turn(toX(p.E), toY(p.N));
      return x > box.x - m && x < box.x + box.w + m && y > box.y - m && y < box.y + box.h + m;
    });
  }, [box]);
  /* past a certain distance, the detail is noise */
  const far = box.w > 2200;
  const timeline = useMemo(() => {
    const inView = shown.map((p) => p.code).filter((c) => byCode.has(c));
    const codes = viewKey === "all" ? register.stands.map((s) => s.code) : [...inView, ...offDrawing.map((s) => s.code)];
    return [...new Set(codes)];
  }, [shown, byCode, viewKey, register.stands, offDrawing]);

  const dayFrom = Math.floor((Date.now() - 2 * 3600_000) / (30 * MIN)) * 30 * MIN;
  const dayTo = dayFrom + 14 * 3600_000;

  const selStand = selected?.kind === "stand" ? selected.code : null;
  const selFlight = selected?.kind === "flight" ? flightById.get(selected.id) : undefined;

  return (
    <Card
      title="Apron view"
      sub="Drag a flight onto a stand to assign it"
      testId="apron-view"
    >
      <div ref={fullRef} className={full ? a.full : undefined} data-testid={full ? "apron-fullscreen" : undefined}>
      <div className={a.toolbar}>
        <div className={a.views} role="group" aria-label={t("Which part of the airfield")}>
          {(Object.keys(VIEWS) as ViewKey[]).map((k) => (
            <button key={k} type="button" aria-pressed={viewKey === k} onClick={() => goTo(k)}>
              {t(VIEWS[k].label)}
            </button>
          ))}
        </div>
        <div className={a.zoom}>
          <button type="button" onClick={() => zoom(1 / 1.4)} aria-label={t("Zoom in")}>
            +
          </button>
          <button type="button" onClick={() => zoom(1.4)} aria-label={t("Zoom out")}>
            −
          </button>
        </div>
        <Button size="sm" onClick={() => setFull((f) => !f)} data-testid="apron-full">
          {full ? `✕ ${t("Leave full screen")}` : `⛶ ${t("Full screen")}`}
        </Button>
        <label className={a.check}>
          <input type="checkbox" checked={drawing} onChange={(e) => setDrawing(e.target.checked)} />
          {t("Survey drawing underneath")}
        </label>
        <label className={a.check}>
          <input type="checkbox" checked={labels} onChange={(e) => setLabels(e.target.checked)} />
          {t("Flight labels")}
        </label>
        <Button size="sm" variant="primary" disabled={!queue.length} onClick={() => setProposal(proposeAll(register, flights, blocks, queue.map((q) => q.flight)))} data-testid="apron-place-all">
          ✦ {t("Place them all")}
        </Button>
      </div>

      <div className={a.timebar}>
        <button
          type="button"
          className={a.play}
          onClick={() => {
            setLive(false);
            setPlaying((p) => !p);
          }}
          aria-label={playing ? t("Pause") : t("Play the day")}
        >
          {playing ? "❚❚" : "▶"}
        </button>
        <input
          type="range"
          min={dayFrom}
          max={dayTo}
          step={5 * MIN}
          value={Math.min(Math.max(atMs, dayFrom), dayTo)}
          onChange={(e) => {
            setLive(false);
            setPlaying(false);
            setAtMs(Number(e.target.value));
          }}
          aria-label={t("The moment the map shows")}
          data-testid="apron-time"
        />
        <span className={a.clock} data-live={live}>
          {hhmm(at.toISOString())}
          <small>{live ? t("live") : t("{n} from now", { n: fromNow(atMs) })}</small>
        </span>
        {!live && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setLive(true);
              setPlaying(false);
              setAtMs(Date.now());
            }}
          >
            {t("Back to now")}
          </Button>
        )}
      </div>

      <p className={a.hintLine}>
        {t("Drag a flight onto a stand, or click a flight and then a stand. The best three stands are numbered; red stands say why they won't work.")}
      </p>

      <div className={a.layout}>
        {/* the flights waiting for a stand */}
        <aside className={a.rail} data-testid="apron-queue">
          <h3>
            {t("Waiting for a stand")} <b>{queue.length}</b>
          </h3>
          {queue.length === 0 ? (
            <p className={a.quiet}>{t("Every visit has a stand.")}</p>
          ) : (
            queue.map((q) => (
              <button
                key={q.flight.id}
                type="button"
                className={a.flightCard}
                data-picked={selected?.kind === "flight" && selected.id === q.flight.id}
                data-testid={`apron-wait-${flightName(q.flight)}`}
                onPointerDown={(e) => startDrag(e, q.flight.id)}
                onKeyDown={(e) => e.key === "Enter" && setSelected({ kind: "flight", id: q.flight.id })}
              >
                <span className={a.tile} style={{ ["--h" as string]: carrierHue(q.flight.carrier) }}>
                  {q.flight.carrier ?? "··"}
                </span>
                <span className={a.cardText}>
                  <b>{q.label}</b>
                  <small>
                    {hhmm(q.from)}–{hhmm(q.to)} · {q.flight.aircraftIcao ?? ""} {q.flight.size ? t("code {c}", { c: q.flight.size }) : ""}
                  </small>
                </span>
                <span className={a.grip} aria-hidden>
                  ⠿
                </span>
              </button>
            ))
          )}
        </aside>

        {/* the airfield */}
        <div className={a.mapWrap}>
          <div className={a.mapBox}>
          <svg
            ref={svgRef}
            className={a.map}
            viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`}
            preserveAspectRatio="xMidYMid meet"
            onPointerDown={onMapDown}
            onPointerMove={onMapMove}
            onPointerUp={onMapUp}
            onPointerCancel={onMapUp}
            data-dragging={Boolean(drag?.moved)}
            style={{ ["--k" as string]: textScale(box) }}
            data-testid="apron-map"
            role="application"
            aria-label={t("Apron map")}
          >
            <defs>
              <pattern id="apron-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="8" height="8" fill="rgba(255,77,79,.18)" />
                <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(255,77,79,.75)" strokeWidth="3" />
              </pattern>
              <path id="apron-aircraft" d={AIRCRAFT} />
            </defs>

            <rect x={-SHEET.w} y={-SHEET.h} width={SHEET.w * 3} height={SHEET.h * 3} className={a.ground} />
            <g transform={`rotate(${ROT} ${PIVOT.x} ${PIVOT.y})`}>
            {drawing && drawingSrc && (
              <image href={drawingSrc} x={0} y={0} width={SHEET.w} height={SHEET.h} opacity={0.55} preserveAspectRatio="none" />
            )}
            {pavement && (
              <>
                <image href={pavement.asphalt} x={0} y={0} width={SHEET.w} height={SHEET.h} preserveAspectRatio="none" opacity={drawing ? 0.5 : 1} />

              </>
            )}

            <g className={a.centrelines}>
              {CENTRELINES.map((d, i) => (
                <path key={i} d={d} />
              ))}
            </g>

            {runways().map((r) => (
              <g key={r.name}>
                <line x1={r.a[0]} y1={r.a[1]} x2={r.b[0]} y2={r.b[1]} className={a.runway} strokeWidth={r.width} />
                <line x1={r.a[0]} y1={r.a[1]} x2={r.b[0]} y2={r.b[1]} className={a.runwayCentre} />
                <text
                  x={(r.a[0] + r.b[0]) / 2}
                  y={(r.a[1] + r.b[1]) / 2}
                  transform={`rotate(${-ROT} ${(r.a[0] + r.b[0]) / 2} ${(r.a[1] + r.b[1]) / 2})`}
                  className={a.runwayName}
                >
                  {t("Runway {r}", { r: r.name })}
                </text>
              </g>
            ))}

            {/* the apron surface under the stands */}
            {shown.map((spot) => (
              <ApronPad key={`pad${spot.code}`} spot={spot} />
            ))}
            {/* the buildings, traced for orientation */}
            <polygon points={terminalOutline()} className={a.terminal} />
            <polygon points={pierOutline()} className={a.pier} />
            {!far && (
              <text
                {...centroid(terminalOutline())}
                transform={`rotate(${-ROT} ${centroid(terminalOutline()).x} ${centroid(terminalOutline()).y})`}
                className={a.buildingName}
              >
                {t("Main terminal")}
              </text>
            )}

            {/* the stands */}
            {shown.map((spot) => (
              <StandGlyph
                key={spot.code}
                spot={spot}
                stand={byCode.get(spot.code)}
                row={rowByCode.get(spot.code)}
                register={register}
                flights={flights}
                flightById={flightById}
                at={at}
                verdict={inHand ? (verdicts.get(spot.code) ?? { clear: false, why: t("not in the register") }) : undefined}
                over={drag?.over === spot.code}
                picked={selStand === spot.code}
                far={far}
                labels={labels}
                onPick={() => pickStand(spot.code)}
                onGrab={(e, id) => startDrag(e, id)}
              />
            ))}
            </g>
          </svg>
          {standsInView(box) === 0 && (
            <div className={a.emptyView} role="status" data-testid="apron-empty-view">
              <span>{t("No stands in this part of the map.")}</span>
              <Button size="sm" variant="primary" onClick={() => goTo(viewKey)}>
                {t("Back to the stands")}
              </Button>
            </div>
          )}
          <span className={a.compass} style={{ transform: `rotate(${ROT}deg)` }} aria-label={t("North")}>
            ↑<b style={{ transform: `rotate(${-ROT}deg)` }}>N</b>
          </span>
        {/* what is picked */}
          {(selStand || selFlight) && (
          <aside className={a.panel} data-testid="apron-panel">
            {selStand ? (
              <StandPanel
                code={selStand}
                stand={byCode.get(selStand)}
                row={rowByCode.get(selStand)}
                register={register}
                flightById={flightById}
                at={at}
                onRelease={(id) => {
                  const asg = register.assignments.find((x) => x.id === id);
                  commit(release(register, id), t("Stand {code} released.", { code: asg?.standCode ?? "" }));
                }}
                onPickFlight={(id) => setSelected({ kind: "flight", id })}
                onAdd={(s) => {
                  commit({ ...register, stands: [...register.stands, s] }, t("Stand {code} added to the register.", { code: s.code }), false);
                }}
                onZoom={() => goToStand(selStand)}
                onClose={() => setSelected(null)}
              />
            ) : selFlight ? (
              <FlightPanel
                flight={selFlight}
                flights={flights}
                register={register}
                blocks={blocks}
                onPut={(code) => dropOn(selFlight, code)}
                onZoom={(code) => goToStand(code)}
                onClose={() => setSelected(null)}
              />
            ) : null}
          </aside>
          )}
          </div>

          {/* the stands the register has and the drawing does not */}
          {offDrawing.length > 0 && (
            <div className={a.offDrawing} data-testid="apron-off-drawing">
              <span className={a.offLabel}>{t("In the register, not on the drawing")}</span>
              {offDrawing.map((s) => {
                const m = standAt(register, s.code, at);
                const v = inHand ? verdicts.get(s.code) : undefined;
                const row = rowByCode.get(s.code);
                return (
                  <button
                    key={s.code}
                    type="button"
                    className={a.chip}
                    data-stand={s.code}
                    data-verdict={v ? (v.clear ? "clear" : "no") : undefined}
                    data-over={drag?.over === s.code}
                    data-picked={selStand === s.code}
                    data-trouble={row && (row.conflicts.length || row.blocked.length || s.outOfService) ? "yes" : undefined}
                    title={v?.why}
                    onClick={() => pickStand(s.code)}
                  >
                    <b>{s.code}</b>
                    <small>{m.now ? flightLabel(flightById, m.now) : t("free")}</small>
                    {v?.rank && <i className={a.rank}>{v.rank}</i>}
                  </button>
                );
              })}
            </div>
          )}
          <Legend />
        </div>

      </div>

      <Timeline
        codes={timeline}
        register={register}
        rows={rowByCode}
        flightById={flightById}
        from={dayFrom}
        to={dayTo}
        at={atMs}
        verdicts={inHand ? verdicts : undefined}
        over={drag?.over ?? null}
        picked={selStand}
        onPick={pickStand}
        onGrab={(e, id) => startDrag(e, id)}
      />
      </div>

      {/* the flight under the pointer while it is dragged */}
      {drag?.moved && inHand && (
        <div ref={ghostRef} className={a.ghost} style={{ transform: `translate(${drag.x + 12}px, ${drag.y + 12}px)` }} aria-hidden>
          <b>{occupancyFor(inHand, flights)?.label ?? flightName(inHand)}</b>
          <small>
            {drag.over
              ? (verdicts.get(drag.over)?.clear
                  ? t("drop on {code}", { code: drag.over })
                  : (verdicts.get(drag.over)?.why ?? t("not in the register")))
              : t("over a stand to place it")}
          </small>
        </div>
      )}

      {confirm && (
        <div className={a.modal} role="dialog" aria-modal aria-label={t("Put it there anyway?")} data-testid="apron-confirm">
          <div className={a.modalBox}>
            <h3>{t("{flight} on stand {code}?", { flight: confirm.occupancy.label, code: confirm.standCode })}</h3>
            <p>{t("The stand will not take it cleanly:")}</p>
            <ul className={a.problems}>
              {confirm.conflicts.map((c) => (
                <li key={`${c.kind}${c.why}`} data-kind={c.kind}>
                  {c.why}
                  {c.ref && (
                    <>
                      {" "}
                      · <Link to="/work-orders">{c.ref}</Link>
                    </>
                  )}
                </li>
              ))}
            </ul>
            <div className={a.modalActions}>
              <Button variant="ghost" onClick={() => setConfirm(null)}>
                {t("Cancel")}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  const p = confirm;
                  setConfirm(null);
                  commit(
                    assign(register, flights, p.flight, p.standCode, who),
                    t("{flight} is on stand {to}, with {n} open.", {
                      flight: p.occupancy.label,
                      to: p.standCode,
                      n: tn(p.conflicts.length, "{n} problem", "{n} problems"),
                    }),
                  );
                  setSelected({ kind: "stand", code: p.standCode });
                }}
              >
                {t("Put it there anyway")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {proposal && (
        <div className={a.modal} role="dialog" aria-modal aria-label={t("Place them all")} data-testid="apron-proposal">
          <div className={a.modalBox}>
            <h3>{t("A stand for every visit still waiting")}</h3>
            {proposal.proposals.length === 0 ? (
              <EmptyState>{t("No clear stand for any of them.")}</EmptyState>
            ) : (
              <ul className={a.proposals}>
                {proposal.proposals.map((p) => (
                  <li key={p.flight.id}>
                    <b>{p.label}</b> → <span className={a.standTag}>{p.standCode}</span>
                    <small>{p.because}</small>
                  </li>
                ))}
              </ul>
            )}
            {proposal.nowhere.length > 0 && (
              <p className={a.bad}>
                {t("No free stand for {list}. They stay in the list.", {
                  list: proposal.nowhere.map((f) => flightName(f)).join(", "),
                })}
              </p>
            )}
            <p className={a.quiet}>
              {t("Contact stands come first, then the smallest stand that fits.")}
            </p>
            <div className={a.modalActions}>
              <Button variant="ghost" onClick={() => setProposal(null)}>
                {t("Cancel")}
              </Button>
              <Button
                variant="primary"
                disabled={!proposal.proposals.length}
                data-testid="apron-apply-all"
                onClick={() => {
                  let next = register;
                  for (const p of proposal.proposals) next = assign(next, flights, p.flight, p.standCode, who);
                  setProposal(null);
                  commit(next, tn(proposal.proposals.length, "{n} visit placed.", "{n} visits placed."));
                }}
              >
                {t("Apply all")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={a.toast} role="status" data-testid="apron-toast" data-failed={toast.failed ? "" : undefined}>
          <span>{toast.text}</span>
          {toast.undo && toast.done && (
            <button
              type="button"
              onClick={() => undoLast(toast.undo!, toast.done!)}
            >
              {t("Undo")}
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

function fromNow(ms: number): string {
  const m = Math.round((ms - Date.now()) / MIN);
  const sign = m < 0 ? "−" : "+";
  const h = Math.floor(Math.abs(m) / 60);
  const r = Math.abs(m) % 60;
  return `${sign}${h ? `${h} h ` : ""}${r} min`;
}

function centroid(points: string): { x: number; y: number } {
  const p = points.split(" ").map((s) => s.split(",").map(Number));
  return { x: p.reduce((n, q) => n + q[0], 0) / p.length, y: p.reduce((n, q) => n + q[1], 0) / p.length };
}

function flightLabel(byId: Map<string, Flight>, asg: StandAssignment): string {
  const f = byId.get(asg.flightId);
  return f ? flightName(f) : asg.flightId;
}

/* ── one stand on the ground ──────────────────────────────────────────── */

/** The concrete a stand sits on, a little larger than its box, so the boxes read as one apron. */
function ApronPad({ spot }: { spot: StandSpot }) {
  const west = spot.area === "west";
  const depth = west ? 70 : 92;
  const half = west ? 40 : 46;
  return (
    <rect
      x={-depth}
      y={-half}
      width={depth + 30}
      height={half * 2}
      transform={`translate(${toX(spot.E)} ${toY(spot.N)}) rotate(${noseAngle(spot.nose)})`}
      className={a.pad}
    />
  );
}

function StandGlyph({
  spot,
  stand,
  row,
  register,
  flights,
  flightById,
  at,
  verdict,
  over,
  picked,
  far,
  labels,
  onPick,
  onGrab,
}: {
  spot: StandSpot;
  stand?: Stand;
  row?: StandRow;
  register: StandRegister;
  flights: readonly Flight[];
  flightById: Map<string, Flight>;
  at: Date;
  verdict?: Verdict;
  over: boolean;
  picked: boolean;
  far: boolean;
  labels: boolean;
  onPick: () => void;
  onGrab: (e: RPointerEvent, flightId: string) => void;
}) {
  const x = toX(spot.E);
  const y = toY(spot.N);
  const angle = noseAngle(spot.nose);
  const west = spot.area === "west";
  const depth = west ? 58 : 72;
  const half = west ? 30 : 36;
  const m = stand ? standAt(register, spot.code, at) : {};
  const occ = m.now ? flightById.get(m.now.flightId) : undefined;
  const dep = occ ? (occ.direction === "departure" ? occ : pairOf(occ, flights)) : undefined;
  const ops = dep ? opsState(dep, at) : undefined;
  const size: SizeCode = occ?.size ?? "C";
  const span = SPAN[size] ?? 36;
  const state = !stand
    ? "unregistered"
    : row?.blocked.length || stand.outOfService
      ? "closed"
      : row?.conflicts.length
        ? "conflict"
        : m.now
            ? "occupied"
            : m.nextIn !== undefined && m.nextIn <= 60
              ? "soon"
              : "free";
  const caution = Boolean(row?.cautions.length);
  const gate = stand?.gate && spot.pier ? gatePoint(spot) : null;
  const foot = spot.pier ? bridgeFoot(spot) : null;
  const upright = -angle - ROT;

  return (
    <g
      data-stand={spot.code}
      data-state={state}
      data-verdict={verdict ? (verdict.clear ? "clear" : "no") : undefined}
      data-over={over}
      data-picked={picked}
      className={a.stand}
      tabIndex={0}
      role="button"
      aria-label={t("Stand {code}", { code: spot.code })}
      onClick={(e) => {
        e.stopPropagation();
        onPick();
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.key === "Enter" && onPick()}
      data-testid={`apron-stand-${spot.code}`}
    >
      {/* the bridge from the pier to the door */}
      {gate && foot && <line x1={foot[0]} y1={foot[1]} x2={gate[0]} y2={gate[1]} className={a.bridge} />}
      <g transform={`translate(${x} ${y}) rotate(${angle})`}>
        <rect x={-depth} y={-half} width={depth + 6} height={half * 2} className={a.box} />
        {state === "closed" && <rect x={-depth} y={-half} width={depth + 6} height={half * 2} fill="url(#apron-hatch)" />}
        {!far && <line x1={-depth - 40} y1={0} x2={0} y2={0} className={a.leadIn} />}
        {!far && <line x1={1} y1={-6} x2={1} y2={6} className={a.stopBar} />}
        {caution && (
          <g transform={`translate(${-depth + 9} ${-half + 9}) rotate(${upright})`} className={a.cautionMark}>
            <path d="M0,-7 L7,6 L-7,6 Z" />
            <text dy="4.6">!</text>
            <title>{row!.cautions.map((b) => `${b.ref ?? ""} ${b.why}`).join("; ")}</title>
          </g>
        )}
        {occ && (
          <g
            className={a.aircraft}
            data-ops={ops}
            onPointerDown={(e) => {
              e.stopPropagation();
              onGrab(e, occ.id);
            }}
          >
            <use href="#apron-aircraft" transform={`translate(-2 0) scale(${span})`} />
            <rect x={-span * 0.97} y={-1.2} width={span * 0.12} height={2.4} style={{ fill: `hsl(${carrierHue(occ.carrier)} 60% 45%)` }} />
          </g>
        )}
        {verdict?.rank && (
          <g transform={`translate(${-depth / 2} 0) rotate(${upright})`}>
            <circle r={13} className={a.rankDot} />
            <text className={a.rankText} dy="0.35em">
              {verdict.rank}
            </text>
          </g>
        )}
        {/* the number painted at the tail end, the right way up */}
        <g transform={`translate(${-depth + 12} ${half - 12}) rotate(${upright})`}>
          <text className={a.standNo} data-unreg={!stand}>
            {spot.code}
          </text>
        </g>
      </g>
      {gate && !far && (
        <g transform={`translate(${gate[0]} ${gate[1]}) rotate(${-ROT})`} className={a.gate} data-ops={ops === "boarding" || ops === "door_late" ? ops : undefined}>
          <rect x={-9} y={-6.5} width={18} height={13} rx={3} />
          <text dy="0.36em">{stand?.gate}</text>
        </g>
      )}
      {labels && !far && (occ || m.next) && (
        <g
          transform={`translate(${x - spot.nose[0] * (depth + 14)} ${y + spot.nose[1] * (depth + 14)}) rotate(${-ROT})`}
          className={a.flightTag}
        >
          {occ ? (
            <text dy="-0.3em">
              {flightName(occ)}
              <tspan className={a.tagTime} dx="4">
                {dep ? `⇢ ${hhmm(bestKnown(dep).at)}` : ""}
              </tspan>
            </text>
          ) : m.next ? (
            <text dy="-0.3em" className={a.tagNext}>
              {t("{flight} in {n} min", { flight: flightLabel(flightById, m.next), n: m.nextIn ?? 0 })}
            </text>
          ) : null}
        </g>
      )}
      {verdict && !verdict.clear && <title>{verdict.why}</title>}
    </g>
  );
}

function Legend() {
  const items: [string, string][] = [
    ["free", "free"],
    ["soon", "a flight within the hour"],
    ["occupied", "occupied"],
    ["conflict", "a clash in the plan"],
    ["closed", "closed by a work order"],
    ["caution", "work nearby"],
    ["unregistered", "on the drawing, not in the register"],
  ];
  return (
    <div className={a.legend}>
      {items.map(([k, label]) => (
        <span key={k} data-state={k}>
          {t(label)}
        </span>
      ))}
    </div>
  );
}

/* ── the stand picked ─────────────────────────────────────────────────── */

const KINDS: StandKind[] = ["contact", "remote", "cargo", "ga", "maintenance"];

function StandPanel({
  code,
  stand,
  row,
  register,
  flightById,
  at,
  onRelease,
  onPickFlight,
  onAdd,
  onZoom,
  onClose,
}: {
  code: string;
  stand?: Stand;
  row?: StandRow;
  register: StandRegister;
  flightById: Map<string, Flight>;
  at: Date;
  onRelease: (assignmentId: string) => void;
  onPickFlight: (flightId: string) => void;
  onAdd: (s: Stand) => void;
  onZoom: () => void;
  onClose: () => void;
}) {
  const spot = STAND_SPOTS.find((s) => s.code === code);
  const [kind, setKind] = useState<StandKind>(spot?.pier ? "contact" : "remote");
  const [size, setSize] = useState<SizeCode>("C");
  const [gate, setGate] = useState("");
  const m = stand ? standAt(register, code, at) : {};
  const mine = register.assignments.filter((x) => x.standCode === code).sort((p, q) => p.from.localeCompare(q.from));

  return (
    <div data-testid={`apron-panel-${code}`}>
      <h3 className={a.panelHead}>
        <span>{t("Stand {code}", { code })}</span>
        <CloseX onClose={onClose} />
      </h3>
      {spot && (
        <button type="button" className={a.linkBtn} onClick={onZoom}>
          {t("zoom to it")}
        </button>
      )}
      {stand ? (
        <dl className={a.facts}>
          <div>
            <dt>{t("Kind")}</dt>
            <dd>{t(STAND_KIND_LABEL[stand.kind])}</dd>
          </div>
          <div>
            <dt>{t("Takes")}</dt>
            <dd>{t("up to code {c}", { c: stand.maxSize })}</dd>
          </div>
          <div>
            <dt>{t("Gate")}</dt>
            <dd>{stand.gate ? `${stand.gate}${stand.bridge ? ` · ${t("air bridge")}` : ""}` : t("none — passengers are bussed")}</dd>
          </div>
          {stand.excludes?.length ? (
            <div>
              <dt>{t("Not with")}</dt>
              <dd>{stand.excludes.join(", ")}</dd>
            </div>
          ) : null}
          <div>
            <dt>{t("Now")}</dt>
            <dd>
              {m.now ? (
                <button type="button" className={a.linkBtn} onClick={() => onPickFlight(m.now!.flightId)}>
                  {flightLabel(flightById, m.now)} {hhmm(m.now.from)}–{hhmm(m.now.to)}
                </button>
              ) : (
                t("free")
              )}
            </dd>
          </div>
        </dl>
      ) : (
        <p className={a.quiet}>{t("This stand is on the airport plan but not in the stand list yet.")}</p>
      )}
      {spot && (
        <p className={a.basis}>
          {t(AREA_LABEL[spot.area])} ·{" "}
          {spot.basis === "drawing"
            ? t("position read from the aerodrome plan, to about 10 m")
            : t("position from the survey's label, to about 30 m")}
          {spot.mars ? ` · ${t("MARS: one wide position or two narrow, L and R")}` : ""}
        </p>
      )}
      {!spot && <p className={a.basis}>{t("Not on the aerodrome plan: its position is not known to the map.")}</p>}

      {(row?.blocked.length || row?.cautions.length || row?.conflicts.length || stand?.outOfService) ? (
        <ul className={a.problems}>
          {stand?.outOfService && <li data-kind="blocked">{stand.outOfService.why}</li>}
          {row?.blocked.map((b) => (
            <li key={`b${b.ref}${b.why}`} data-kind="blocked">
              {b.ref && <Link to="/work-orders">{b.ref}</Link>} {b.why}
            </li>
          ))}
          {row?.conflicts.map((c) => (
            <li key={`c${c.kind}${c.why}`} data-kind={c.kind}>
              {c.why}
            </li>
          ))}
          {row?.cautions.map((b) => (
            <li key={`w${b.ref}${b.why}`} data-kind="caution">
              {b.ref && <Link to="/work-orders">{b.ref}</Link>} {b.why}
            </li>
          ))}
        </ul>
      ) : null}

      {stand && (
        <>
          <h4 className={a.sub}>{t("On it today")}</h4>
          {mine.length === 0 ? (
            <p className={a.quiet}>{t("nothing")}</p>
          ) : (
            <ul className={a.visits}>
              {mine.map((v) => (
                <li key={v.id}>
                  <button type="button" className={a.linkBtn} onClick={() => onPickFlight(v.flightId)}>
                    {flightLabel(flightById, v)}
                  </button>
                  <span>
                    {hhmm(v.from)}–{hhmm(v.to)} {v.actual ? t("(there now)") : ""}
                  </span>
                  {!v.actual && (
                    <button type="button" className={a.release} onClick={() => onRelease(v.id)} data-testid={`apron-release-${v.id}`}>
                      {t("Release")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {!stand && spot && (
        <form
          className={a.addForm}
          onSubmit={(e) => {
            e.preventDefault();
            onAdd({
              code,
              name: t("Stand {code}", { code }),
              kind,
              maxSize: size,
              zoneCode: spot.area === "west" ? "APRON-W" : spot.area === "north" ? "APRON-N" : "APRON-1",
              bridge: kind === "contact" && spot.pier ? true : undefined,
              gate: gate.trim() || undefined,
            });
          }}
        >
          <h4 className={a.sub}>{t("Add it to the register")}</h4>
          <Field label="Kind">
            <Select value={kind} onChange={(e) => setKind(e.target.value as StandKind)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(STAND_KIND_LABEL[k])}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Largest aircraft">
            <Select value={size} onChange={(e) => setSize(e.target.value as SizeCode)}>
              {SIZE_ORDER.map((c) => (
                <option key={c} value={c}>
                  {t("code {c}", { c })}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Gate (if it has one)">
            <Input value={gate} onChange={(e) => setGate(e.target.value)} />
          </Field>
          <Button type="submit" size="sm" variant="primary" data-testid="apron-add-stand">
            {t("Add stand {code}", { code })}
          </Button>
        </form>
      )}
    </div>
  );
}

/** The way out of a panel: a plain ✕, large enough to hit on a phone. */
function CloseX({ onClose }: { onClose: () => void }) {
  return (
    <button type="button" className={a.closeX} onClick={onClose} aria-label={t("Close")} data-testid="apron-panel-close">
      ✕
    </button>
  );
}

/* ── the flight picked ────────────────────────────────────────────────── */

function FlightPanel({
  flight,
  flights,
  register,
  blocks,
  onPut,
  onZoom,
  onClose,
}: {
  flight: Flight;
  flights: readonly Flight[];
  register: StandRegister;
  blocks: readonly StandBlock[];
  onPut: (code: string) => void;
  onZoom: (code: string) => void;
  onClose: () => void;
}) {
  const occ = occupancyFor(flight, flights);
  const own = assignmentOf(register, flight, flights);
  const visit = occ ? (flights.find((f) => f.id === occ.flightId) ?? flight) : flight;
  const reg = own ? release(register, own.id) : register;
  const options = occ
    ? candidatesFor({ register: reg, flights, blocks, flight: visit, window: { from: occ.from, to: occ.to } })
    : [];
  const clear = options.filter((o) => o.clear).slice(0, 5);
  const blockedTop = options.filter((o) => !o.clear).slice(0, 3);
  return (
    <div data-testid="apron-flight-panel">
      <h3 className={a.panelHead}>
        <span>{occ?.label ?? flightName(flight)}</span>
        <CloseX onClose={onClose} />
      </h3>
      <p className={a.quiet}>
        {flight.aircraftIcao ?? ""} {flight.size ? t("code {c}", { c: flight.size }) : ""} ·{" "}
        {occ ? `${hhmm(occ.from)}–${hhmm(occ.to)}` : t("no time on the record")}
        {own ? (
          <>
            {" "}
            · {t("now on")}{" "}
            <button type="button" className={a.linkBtn} onClick={() => onZoom(own.standCode)}>
              {own.standCode}
            </button>
          </>
        ) : (
          ` · ${t("no stand yet")}`
        )}
      </p>
      <h4 className={a.sub}>{t("Best stands for it")}</h4>
      {clear.length === 0 ? (
        <p className={a.bad}>{t("No stand is clear for the whole visit.")}</p>
      ) : (
        <ol className={a.options}>
          {clear.map((o) => (
            <li key={o.stand.code}>
              <span className={a.standTag}>{o.stand.code}</span>
              <small>{o.because}</small>
              <Button size="sm" onClick={() => onPut(o.stand.code)} data-testid={`apron-put-${o.stand.code}`}>
                {t("Put it here")}
              </Button>
            </li>
          ))}
        </ol>
      )}
      {blockedTop.length > 0 && (
        <>
          <h4 className={a.sub}>{t("Why not the others")}</h4>
          <ul className={a.problems}>
            {blockedTop.map((o) => (
              <li key={o.stand.code} data-kind={o.conflicts[0]?.kind}>
                <b>{o.stand.code}</b> {o.conflicts.map((c) => c.why).join(" ")}
              </li>
            ))}
          </ul>
        </>
      )}
      <p className={a.quiet}>{t("Pick a stand on the map to put it there, or drag it.")}</p>
    </div>
  );
}

/* ── the timeline under the map ───────────────────────────────────────── */

function Timeline({
  codes,
  register,
  rows,
  flightById,
  from,
  to,
  at,
  verdicts,
  over,
  picked,
  onPick,
  onGrab,
}: {
  codes: string[];
  register: StandRegister;
  rows: Map<string, StandRow>;
  flightById: Map<string, Flight>;
  from: number;
  to: number;
  at: number;
  verdicts?: Map<string, Verdict>;
  over: string | null;
  picked: string | null;
  onPick: (code: string) => void;
  onGrab: (e: RPointerEvent, flightId: string) => void;
}) {
  const span = to - from;
  const pct = (ms: number) => Math.max(0, Math.min(100, ((ms - from) / span) * 100));
  const hours: number[] = [];
  for (let h = Math.ceil(from / 3600_000) * 3600_000; h < to; h += 3600_000) hours.push(h);
  if (!codes.length) return null;
  return (
    <div className={a.timeline} data-testid="apron-timeline">
      <div className={a.tlHead}>
        <span />
        <div className={a.tlAxis}>
          {hours.map((h) => (
            <span key={h} style={{ insetInlineStart: `${pct(h)}%` }}>
              {hhmm(new Date(h).toISOString())}
            </span>
          ))}
        </div>
      </div>
      {codes.map((code) => {
        const row = rows.get(code);
        const v = verdicts?.get(code);
        const mine = register.assignments.filter((x) => x.standCode === code && Date.parse(x.to) > from && Date.parse(x.from) < to);
        const trouble = row && (row.conflicts.length > 0 || row.blocked.length > 0);
        return (
          <div
            key={code}
            className={a.tlRow}
            data-stand={code}
            data-verdict={v ? (v.clear ? "clear" : "no") : undefined}
            data-over={over === code}
            data-picked={picked === code}
            onClick={() => onPick(code)}
          >
            <span className={a.tlName} data-trouble={trouble ? "yes" : undefined}>
              {code}
              {v?.rank && <i className={a.rank}>{v.rank}</i>}
            </span>
            <div className={a.tlLane}>
              {row?.blocked.map((b, i) => (
                <span
                  key={`b${i}`}
                  className={a.tlBlock}
                  style={{ insetInlineStart: `${pct(b.from ? Date.parse(b.from) : from)}%`, width: `${pct(b.to ? Date.parse(b.to) : to) - pct(b.from ? Date.parse(b.from) : from)}%` }}
                  title={`${b.ref ?? ""} ${b.why}`}
                />
              ))}
              {mine.map((x) => {
                const f = flightById.get(x.flightId);
                const clash = row?.conflicts.some((c) => c.otherFlightId === x.flightId || c.otherFlightId === undefined && c.kind === "occupied");
                return (
                  <span
                    key={x.id}
                    className={a.tlBar}
                    data-actual={x.actual}
                    data-clash={clash ? "yes" : undefined}
                    style={{
                      insetInlineStart: `${pct(Date.parse(x.from))}%`,
                      width: `${Math.max(1.2, pct(Date.parse(x.to)) - pct(Date.parse(x.from)))}%`,
                      ["--h" as string]: carrierHue(f?.carrier),
                    }}
                    title={`${f ? flightName(f) : x.flightId} ${hhmm(x.from)}–${hhmm(x.to)}`}
                    onPointerDown={(e) => !x.actual && onGrab(e, x.flightId)}
                  >
                    {f ? flightName(f) : ""}
                  </span>
                );
              })}
              <span className={a.tlNow} style={{ insetInlineStart: `${pct(at)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
